import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { PageContainer } from "../../components/page_container/PageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
    generatePassword,
    lengthOnChanged,
    numbersOnChanged,
    outputCopiedTapped,
    selectState,
    symbolsOnChanged,
    uppercaseOnChanged,
} from "./PasswordGeneratorSlice";

import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import {
    Box,
    Divider,
    FormControlLabel,
    Input,
    Slider,
    Stack,
    Switch,
    TextField,
    Typography,
} from "@mui/material";
import { HomeCard } from "../../components/home_card/HomeCard";

import QrCodeGeneratorImage from "../../assets/QrCodeGenerator.webp";
import FaviconGeneratorImage from "../../assets/FaviconGenerator.webp";
import KeyIcon from "@mui/icons-material/Key";
import { CustomButton } from "../../components/custom_button/CustomButton";
import { useTranslation } from "react-i18next";
import { ResultCard } from "../../components/result_card/ResultCard";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function PasswordGenerator() {
    const dispatch = useAppDispatch();
    const state = useAppSelector(selectState);
    const { t } = useTranslation();

    return (
        <SideMenuPageContainer
            pageTitle={t("passwordGenerator.title")}
            pageSeoTitle={t("passwordGenerator.seoTitle")}
            pageDescription={t("passwordGenerator.description")}
        >
            <Box sx={{ width: { xs: 300, sm: 350, md: 600, lg: 700 } }}>
                <Grid2
                    container
                    spacing={2}
                    sx={{ mt: 2 }}
                    columns={{ xs: 6, sm: 6, md: 12, lg: 12 }}
                >
                    <Grid2 xs={12}>
                        <Typography variant="h2" sx={{ mb: 2 }}>
                            {t("passwordGenerator.mainHeading")}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            <b style={{ color: "#FFD9A8" }}>{t("passwordGenerator.step1")}</b> {t("passwordGenerator.step1Description")}
                            <br />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Stack direction="row" justifyContent="center" alignItems="start">
                            <Stack direction="row" justifyContent="center" alignItems="start">
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={state.uppercase}
                                            onChange={(e) => {
                                                dispatch(uppercaseOnChanged(Boolean(e.target.checked)));
                                            }}
                                        />
                                    }
                                    label={t("passwordGenerator.options.uppercase")}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={state.numbers}
                                            onChange={(e) => {
                                                dispatch(numbersOnChanged(Boolean(e.target.checked)));
                                            }}
                                        />
                                    }
                                    label={t("passwordGenerator.options.numbers")}
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={state.symbols}
                                            onChange={(e) => {
                                                dispatch(symbolsOnChanged(Boolean(e.target.checked)));
                                            }}
                                        />
                                    }
                                    label={t("passwordGenerator.options.symbols")}
                                />
                            </Stack>
                        </Stack>

                        <Grid2
                            container
                            xs={12}
                            sx={{ width: "350", mt: 2 }}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Grid2>
                                <Typography variant="body1" sx={{ mb: 3 }}>
                                    {t("passwordGenerator.lengthLabel")}
                                </Typography>
                                <Slider
                                    sx={{
                                        mt: 4,
                                        '& .MuiSlider-markLabel': {
                                            color: '#FFD9A8',          // change text color
                                        },
                                    }}
                                    value={state.length}
                                    onChange={(_, value) =>
                                        dispatch(
                                            lengthOnChanged(
                                                typeof value === "number" ? value : value[0]
                                            )
                                        )
                                    }
                                    min={4}
                                    max={50}
                                    step={1}
                                    valueLabelDisplay="on"
                                    marks={[
                                        { value: 4, label: "4" },
                                        { value: 50, label: "50" },
                                    ]}
                                />
                            </Grid2>
                        </Grid2>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="body1" sx={{ mb: 0.5 }}>
                            <b style={{ color: "#FFD9A8" }}>{t("passwordGenerator.step2")}</b> {t("common.generate")}
                            <br />
                        </Typography>
                    </Grid2>
                    <Grid2 xs={12}>
                        <Stack
                            direction="column"
                            justifyContent="center"
                            alignItems="center"
                            sx={{ width: "350", mb: 2 }}
                        >
                            <CustomButton
                                icon={<KeyIcon sx={{ color: "#FFD9A8" }} />}
                                label={t("passwordGenerator.generate")}
                                width={180}
                                onClick={() => {
                                    dispatch(generatePassword());
                                }}
                            />
                        </Stack>

                        <Stack sx={{ display: state.output != "" ? "flex" : "none" }}>
                            <ResultCard
                                value={state.output}
                                smallResult={true}
                                description={t("passwordGenerator.resultLabel")}
                                symbol={""}
                                resultCopied={state.outputCopied}
                                onCopy={() => {
                                    dispatch(outputCopiedTapped());
                                }}
                            />
                        </Stack>
                    </Grid2>

                    <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
                        <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
                    </Grid2>
                    <Grid2 xs={12}>
                        <AdSense
                            adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                            adFormat="fluid"
                            adLayoutKey="-fb+5w+4e-db+86"
                            sx={{ mb: 3 }}
                            />
                        <Typography variant="h3" sx={{ mb: 2 }}>
                            {t("passwordGenerator.content.freeHeading")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {t("passwordGenerator.content.freeDescription")}
                        </Typography>
                        <Typography variant="h3" sx={{ mb: 2 }}>
                            {t("passwordGenerator.content.privacyHeading")}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            {t("passwordGenerator.content.privacyDescription")}
                        </Typography>
                    </Grid2>

                    <Grid2 xs={12}>
                        <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
                            {t("passwordGenerator.related")}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={6}>
                        <HomeCard
                            languageResource="faviconGenerator"
                            link="/FaviconGenerator"
                            smallCard={true}
                            image={FaviconGeneratorImage}
                        />
                    </Grid2>
                    <Grid2 xs={6}>
                        <HomeCard
                            languageResource="qrCodeGenerator"
                            link="/QrCodeGenerator"
                            smallCard={true}
                            image={QrCodeGeneratorImage}
                        />
                    </Grid2>
                </Grid2>
            </Box>
        </SideMenuPageContainer>
    );
}
